// src/components/MobileProtectedRoute.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import api from '../services/api';
import { 
  determineDestinationRoute, 
  shouldRedirect, 
  validateTokenScope,
  MOBILE_ROUTE_CONFIG 
} from '../config/mobileRoutes';

const MobileProtectedRoute = ({ children }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    // Reset state when token OR location changes
    setIsValidating(true);
    setIsValid(false);
    validateTokenAccess();
  }, [token, location.pathname]);

  // Verificar si la ruta actual está permitida para el tipo de token
  const isCurrentRouteAllowed = (tokenType) => {
    const currentPath = location.pathname;
    const config = MOBILE_ROUTE_CONFIG.TOKEN_DESTINATIONS[tokenType];
    
    if (!config) return false;
    
    // Verificar si es un entry point (estas rutas son válidas pero necesitan redirección)
    const isEntryPoint = MOBILE_ROUTE_CONFIG.ENTRY_POINT_PATHS.some(entryPath => {
      const pathPattern = entryPath.replace(':token', token);
      return currentPath === pathPattern;
    });
    
    if (isEntryPoint) {
      return true; // Los entry points son válidos, pero se redirigen después
    }
    
    // Verificar usando allowedRoutes
    if (config.allowedRoutes && config.allowedRoutes.length > 0) {
      return config.allowedRoutes.some(allowedRoute => {
        const routePattern = allowedRoute.replace(':token', token);
        return currentPath === routePattern;
      });
    }
    
    // Fallback: verificar basePath (para retrocompatibilidad)
    return currentPath.startsWith(`${config.basePath}/${token}`);
  };

  // Redirigir a la ruta correcta para el tipo de token
  const redirectToCorrectRoute = (tokenType) => {
    const config = MOBILE_ROUTE_CONFIG.TOKEN_DESTINATIONS[tokenType];
    if (config && config.requiresRedirect) {
      const correctPath = `${config.basePath}/${token}${config.targetPath}`;
      navigate(correctPath, { replace: true });
    }
  };

  const validateTokenAccess = async () => {
    try {
      await performTokenValidation();
    } catch (error) {
      console.error('Error validating token access:', error);
      handleTokenError(error);
    }
  };

  const performTokenValidation = async () => {
    try {
      const response = await api.post('/temporary-token/validate', { token });
      
      // Guardamos datos básicos en localStorage con prefijo temp_ para evitar conflicto con sesión del vendedor
      localStorage.setItem('temp_authToken', response.data.access_token);
      localStorage.setItem('temp_tokenScope', JSON.stringify(response.data.scope));
      localStorage.setItem('temp_tokenType', response.data.type);
      localStorage.setItem('temp_tokenLastValidation', Date.now().toString());
      
      const { type } = response.data;
      const tokenScope = response.data.scope || {};
      
      // Extraer y guardar IDs del scope
      const { sale_id, customer_id, purchase_request_id, branch_id, handoff } = tokenScope;
      const customerDni = tokenScope.customer_dni
        || tokenScope.dni
        || tokenScope.document_number
        || tokenScope.customer?.dni
        || handoff?.customer?.dni;
      if (sale_id) localStorage.setItem('temp_tokenSaleId', sale_id);
      if (customer_id) localStorage.setItem('temp_tokenCustomerId', customer_id);
      if (purchase_request_id) localStorage.setItem('temp_tokenPurchaseRequestId', purchase_request_id);
      if (branch_id) localStorage.setItem('temp_tokenBranchId', branch_id);
      if (customerDni) {
        localStorage.setItem('temp_tokenDni', customerDni);
      } else {
        localStorage.removeItem('temp_tokenDni');
      }
      if (handoff && typeof handoff === 'object') {
        localStorage.setItem('temp_handoffContext', JSON.stringify(handoff));
      } else {
        localStorage.removeItem('temp_handoffContext');
      }
      
      // Validar scope
      if (!validateTokenScope(type, tokenScope)) {
        handleTokenError(new Error('Scope inválido para el tipo de token'));
        return;
      }
      
      // Verificar redirección
      const currentPath = location.pathname;
      const fromStep = searchParams.get('from_step');
      
      if (shouldRedirect(currentPath, token, type)) {
        const expectedRoute = determineDestinationRoute(token, response.data.scope, type, fromStep);
        navigate(expectedRoute, { replace: true });
        return;
      }
      
      // Verificar permisos de ruta
      if (!isCurrentRouteAllowed(type)) {
        redirectToCorrectRoute(type);
        return;
      }
      
      setIsValid(true);
      setIsValidating(false);

    } catch (error) {
      console.error('Error performing token validation:', error);
      handleTokenError(error);
    }
  };

  const handleTokenError = (error) => {
    // Limpiar localStorage de tokens temporales (con prefijo temp_)
    localStorage.removeItem('temp_authToken');
    localStorage.removeItem('temp_tokenScope');
    localStorage.removeItem('temp_tokenType');
    localStorage.removeItem('temp_tokenSaleId');
    localStorage.removeItem('temp_tokenCustomerId');
    localStorage.removeItem('temp_tokenPurchaseRequestId');
    localStorage.removeItem('temp_tokenBranchId');
    localStorage.removeItem('temp_tokenDni');
    localStorage.removeItem('temp_handoffContext');
    localStorage.removeItem('temp_tokenLastValidation');

    setIsValidating(false);
    setIsValid(false);

    // Determinar tipo de error y redirigir apropiadamente
    const errorType = error.response?.data?.message?.includes('expired')
      ? 'token_expired'
      : 'invalid_token';
    
    navigate(`/m/error/${errorType}`, { replace: true });
  };

  // Mostrar spinner mientras validamos
  if (isValidating) {
    return (
      <div className="container-fluid vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Validando acceso...</span>
          </div>
          <p className="text-muted">Verificando permisos...</p>
        </div>
      </div>
    );
  }

  // Si la validación falla, no renderizar nada (ya redirigimos)
  if (!isValid) {
    return null;
  }

  // Si todo está bien, renderizar el componente hijo
  return children;
};

export default MobileProtectedRoute;